import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient.js";
import "../../styles/WaxSetting.css";
import QRCode from "qrcode";

/* ============================================================
   PRINT LAYOUT (A5 style, same as before — QR updated)
============================================================ */
const PrintLayout = ({ lotNo, date, workerName, blocks }) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState("");

  useEffect(() => {
    const qrUrl = `https://wax.aynajewels.in/wax-receive?lot=${lotNo}`;
    QRCode.toDataURL(qrUrl, { width: 180, margin: 2 }, (err, url) => {
      if (!err) setQrCodeDataURL(url);
    });
  }, [lotNo]);

  return (
    <div
      className="print-wrapper print-only-content"
      style={{
        padding: "20px",
        fontSize: "18px",
        width: "700px",
        margin: "0 auto",
      }}
    >
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <h2 style={{ margin: 0 }}>
            <strong>Lot No:</strong> {lotNo}
          </h2>
          <h2 style={{ marginTop: 10 }}>
            <strong>Date:</strong> {date}
          </h2>
          <h2 style={{ marginTop: 10 }}>
            <strong>Worker:</strong> {workerName}
          </h2>
        </div>

        {qrCodeDataURL && (
          <div>
            <img
              src={qrCodeDataURL}
              alt="QR Code"
              style={{
                width: 135,
                height: 135,
                border: "2px solid #000",
              }}
            />
          </div>
        )}
      </div>

      {/* BLOCKS */}
      {blocks.map((b, index) => (
        <div
          key={index}
          style={{
            marginTop: 28,
            border: "2px solid black",
            width: "560px",
          }}
        >
          {/* HEADER ROW */}
          <div
            style={{
              display: "flex",
              width: "100%",
              borderBottom: "2px solid black",
              background: "#f5f5f5",
              fontWeight: "bold",
            }}
          >
            <div
              style={{
                width: "12%",
                borderRight: "2px solid black",
                padding: "8px",
              }}
            >
              #
            </div>
            <div style={{ width: "88%", padding: "8px" }}>Details</div>
          </div>

          {/* DETAILS ROW */}
          <div style={{ display: "flex", width: "100%" }}>
            <div
              style={{
                width: "12%",
                borderRight: "2px solid black",
                padding: "8px",
              }}
            >
              {index + 1}
            </div>

            <div
              style={{
                width: "88%",
                padding: "8px",
                fontSize: "16px",
                lineHeight: "22px",
              }}
            >
              <strong>{b.item_name}</strong> ({b.design_code || "N/A"})
              <br />
              Gross: {b.gross_wt || 0}g | Net: {b.net_wt || 0}g | Pcs:{" "}
              {b.pieces || 0}
              <br />
              Boxes: {b.no_boxes || 0} × {b.per_box_wt || 0} ={" "}
              {b.total_box_wt || 0} | Bags: {b.no_bags || 0} ×{" "}
              {b.per_bag_wt || 0} = {b.total_bag_wt || 0}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ============================================================
   MAIN COMPONENT
============================================================ */
export default function IssueWax() {
  const [lotNo, setLotNo] = useState("");
  const [date, setDate] = useState("");

  const [workerId, setWorkerId] = useState("");
  const [workerName, setWorkerName] = useState("");

  const [workers, setWorkers] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [blocks, setBlocks] = useState([]);

  const params = new URLSearchParams(window.location.search);
  const continueLot = params.get("continueLot");

  useEffect(() => {
    setDate(new Date().toISOString().slice(0, 10));
    fetchWorkers();
    fetchItems();

    if (continueLot) {
      setLotNo(continueLot);
      loadExistingItems(continueLot);
    } else {
      genLotNo();
      setBlocks([createEmptyBlock()]);
    }
  }, []);

  useEffect(() => {
    const w = workers.find((w) => w.id === workerId);
    setWorkerName(w ? w.worker_name : "");
  }, [workerId, workers]);

  /* ============================================================
     FETCH FUNCTIONS
  ============================================================ */
  const fetchWorkers = async () => {
    const { data } = await supabase.from("WORKERS").select("*");
    setWorkers(data || []);
  };

  const fetchItems = async () => {
    const { data } = await supabase.from("ITEMS").select("*");
    setItemsList(data || []);
  };

  /* ============================================================
     HELPERS
  ============================================================ */
  const createEmptyBlock = () => ({
    id: Date.now(),
    item_id: "",
    item_name: "",
    design_code: "",
    gross_wt: "",
    no_boxes: "",
    per_box_wt: "",
    total_box_wt: "",
    no_bags: "",
    per_bag_wt: "",
    total_bag_wt: "",
    net_wt: "",
    pieces: "",
    avg_piece_wt: "",
    existing: false,
  });

  const genLotNo = async () => {
    const { count } = await supabase.from("WAX_LOTS").select("*", { count: "exact" });
    const next = (count || 0) + 1;
    setLotNo(`WAX-${String(next).padStart(4, "0")}`);
  };

  const loadExistingItems = async (lotNo) => {
    const { data: lotRow } = await supabase
      .from("WAX_LOTS")
      .select("id")
      .eq("lot_no", lotNo)
      .single();

    if (!lotRow) return;

    const { data: items } = await supabase
      .from("WAX_ITEMS")
      .select("*")
      .eq("lot_id", lotRow.id);

    if (!items) return;

    const mapped = items.map((i) => {
      const it = itemsList.find((d) => d.id === i.item_id);
      return {
        id: Date.now() + Math.random(),
        item_id: i.item_id,
        item_name: it?.item_name || "",
        design_code: i.design_code,
        gross_wt: i.gross_weight,
        no_boxes: i.boxes_count,
        per_box_wt: i.per_box_weight,
        total_box_wt: i.total_box_weight,
        no_bags: i.bags_count,
        per_bag_wt: i.per_bag_weight,
        total_bag_wt: i.total_bag_weight,
        net_wt: i.net_weight,
        pieces: i.pieces_count,
        avg_piece_wt: i.avg_piece_weight,
        existing: true,
      };
    });

    setBlocks([...mapped, createEmptyBlock()]);
  };

  /* ============================================================
     REMOVE BLOCK
  ============================================================ */
  const removeBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  /* ============================================================
     UPDATE BLOCK
  ============================================================ */
  const updateBlock = (id, field, value) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== id) return b;

        let u = { ...b, [field]: value };

        if (field === "item_id") {
          const it = itemsList.find((x) => x.id === value);
          u.item_name = it?.item_name || "";
        }

        u.total_box_wt = (Number(u.no_boxes) || 0) * (Number(u.per_box_wt) || 0);
        u.total_bag_wt = (Number(u.no_bags) || 0) * (Number(u.per_bag_wt) || 0);
        u.net_wt = (Number(u.gross_wt) || 0) - u.total_bag_wt;

        if (u.pieces) {
          u.avg_piece_wt = (u.net_wt / Number(u.pieces)).toFixed(3);
        } else u.avg_piece_wt = 0;

        return u;
      })
    );
  };

  /* ============================================================
     SAVE + PRINT
  ============================================================ */
  const handleSaveAndPrint = async () => {
    if (!workerId) return alert("Select worker");

    let lotId = null;

    if (!continueLot) {
      const { data: lotData } = await supabase
        .from("WAX_LOTS")
        .insert([{ lot_no: lotNo, worker_id: workerId, issue_date: date, status: "ISSUED" }])
        .select()
        .single();

      lotId = lotData.id;
    } else {
      const { data } = await supabase
        .from("WAX_LOTS")
        .select("id")
        .eq("lot_no", lotNo)
        .single();

      lotId = data.id;
    }

    const newItems = blocks.filter((b) => !b.existing && b.item_id);

    if (newItems.length > 0) {
      await supabase.from("WAX_ITEMS").insert(
        newItems.map((b) => ({
          lot_id: lotId,
          item_id: b.item_id,
          design_code: b.design_code,

          // ORIGINAL ISSUE DATA
          issued_gross: Number(b.gross_wt),
          issued_net: Number(b.net_wt),
          issued_pcs: Number(b.pieces),

          // ENTRY STATIC
          gross_weight: Number(b.gross_wt),
          net_weight: Number(b.net_wt),
          pieces_count: Number(b.pieces),
          boxes_count: Number(b.no_boxes),
          per_box_weight: Number(b.per_box_wt),
          total_box_weight: Number(b.total_box_wt),
          bags_count: Number(b.no_bags),
          per_bag_weight: Number(b.per_bag_wt),
          total_bag_weight: Number(b.total_bag_wt),
          avg_piece_weight: Number(b.avg_piece_wt),

          // RECEIVED VALUES INITIALLY NULL
          received_gross: null,
          received_net: null,
          received_pcs: null,
        }))
      );
    }

    // PRINT
    setTimeout(() => {
      window.print();
    }, 250);

    // REFRESH PAGE
    setTimeout(() => {
      window.location.href = "/wax-issue";
    }, 950);
  };

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <div style={{ padding: 30 }}>
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            .print-only-content, .print-only-content * {
              visibility: visible !important;
            }
            .print-only-content {
              position: absolute;
              left: 0;
              top: 0;
            }
          }
        `}
      </style>

      <div className="wax-card" style={{ padding: 25 }}>
        <h2 style={{ textAlign: "center" }}>
          {continueLot ? "Add Items to Existing Lot" : "Issue Wax"}
        </h2>

        {/* TOP FIELDS */}
        <div className="flex-row">
          <div style={{ flex: 1 }}>
            <label>Lot No</label>
            <input className="wax-input" value={lotNo} readOnly />
          </div>

          <div style={{ flex: 1 }}>
            <label>Date</label>
            <input className="wax-input" value={date} readOnly />
          </div>

          <div style={{ flex: 1 }}>
            <label>Worker</label>
            <select
              className="wax-select"
              value={workerId}
              onChange={(e) => setWorkerId(e.target.value)}
            >
              <option value="">Select Worker</option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.worker_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ITEMS */}
        {blocks.map((b, i) => (
          <div
            key={b.id}
            className="wax-block"
            style={{
              border: "1px solid #e6d8b9",
              borderRadius: 10,
              padding: 20,
              marginTop: 25,
            }}
          >
            <div className="flex-row-between">
              <strong>Item #{i + 1}</strong>

              {!b.existing && (
                <button className="wax-btn delete" onClick={() => removeBlock(b.id)}>
                  Remove
                </button>
              )}
            </div>

            <label>Item</label>
            <select
              className="wax-select"
              value={b.item_id}
              onChange={(e) => updateBlock(b.id, "item_id", e.target.value)}
            >
              <option value="">Select Item</option>
              {itemsList.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.item_name}
                </option>
              ))}
            </select>

            <label>Design Code</label>
            <input
              className="wax-input"
              value={b.design_code}
              onChange={(e) => updateBlock(b.id, "design_code", e.target.value)}
            />

            <label>Gross Weight</label>
            <input
              className="wax-input"
              type="number"
              value={b.gross_wt}
              onChange={(e) => updateBlock(b.id, "gross_wt", e.target.value)}
            />

            <label>Boxes Count</label>
            <input
              className="wax-input"
              type="number"
              value={b.no_boxes}
              onChange={(e) => updateBlock(b.id, "no_boxes", e.target.value)}
            />

            <label>Per Box Wt</label>
            <input
              className="wax-input"
              type="number"
              value={b.per_box_wt}
              onChange={(e) => updateBlock(b.id, "per_box_wt", e.target.value)}
            />

            <label>Bags Count</label>
            <input
              className="wax-input"
              type="number"
              value={b.no_bags}
              onChange={(e) => updateBlock(b.id, "no_bags", e.target.value)}
            />

            <label>Per Bag Wt</label>
            <input
              className="wax-input"
              type="number"
              value={b.per_bag_wt}
              onChange={(e) => updateBlock(b.id, "per_bag_wt", e.target.value)}
            />

            <label>Pieces</label>
            <input
              className="wax-input"
              type="number"
              value={b.pieces}
              onChange={(e) => updateBlock(b.id, "pieces", e.target.value)}
            />
          </div>
        ))}

        {/* TOTALS */}
        <div style={{ marginTop: 25, fontWeight: "bold", textAlign: "center" }}>
          TOTAL NET:{" "}
          {blocks
            .reduce((a, b) => a + (Number(b.net_wt) || 0), 0)
            .toFixed(3)}
          g &nbsp;|&nbsp; TOTAL GROSS:{" "}
          {blocks
            .reduce((a, b) => a + (Number(b.gross_wt) || 0), 0)
            .toFixed(3)}
          g &nbsp;|&nbsp; TOTAL PIECES:{" "}
          {blocks.reduce((a, b) => a + (Number(b.pieces) || 0), 0)}
        </div>

        {/* BUTTONS */}
        <div className="flex-row" style={{ marginTop: 20, gap: 20 }}>
          <button className="wax-btn" style={{ flex: 1 }} onClick={() => setBlocks([...blocks, createEmptyBlock()])}>
            + Add Item
          </button>

          <button className="wax-btn" style={{ flex: 1 }} onClick={handleSaveAndPrint}>
            Save & Print
          </button>
        </div>
      </div>

      {/* PRINT */}
      {lotNo && workerName && (
        <PrintLayout
          lotNo={lotNo}
          date={date}
          workerName={workerName}
          blocks={blocks}
        />
      )}
    </div>
  );
}
